import {
  createContext,
  type FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

export type ChildFormHandler = (
  event?: FormEvent<HTMLFormElement>,
) => Promise<unknown> | unknown;

export type ChildFormsContextValue = {
  handlers: Record<string, ChildFormHandler>;
};

export const ChildFormsContext = createContext<
  ChildFormsContextValue | undefined
>(undefined);

export function useChildFormsController() {
  const state = useMemo(() => ({ handlers: {} }) as ChildFormsContextValue, []);
  const executeHandlers = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      return Promise.all(
        Object.values(state.handlers).map((handler) => handler(event)),
      );
    },
    [state],
  );
  return {
    state,
    executeHandlers,
  };
}

export function useChildFormHandler(id: string, handler: ChildFormHandler) {
  const context = useContext(ChildFormsContext);
  useEffect(() => {
    if (!context) {
      throw new Error(
        'useChildFormHandler must be used within a ChildFormsProvider',
      );
    }

    context.handlers[id] = handler;
    return () => {
      delete context.handlers[id];
    };
  }, [context, id, handler]);
}

/**
 * Optional variant that only registers when context exists (e.g. in drawer vs detail view).
 * Use when the component is rendered in both ChildFormsContext and non-context environments.
 */
export function useOptionalChildFormHandler(
  id: string,
  handler: ChildFormHandler,
) {
  const context = useContext(ChildFormsContext);
  useEffect(() => {
    if (context) {
      context.handlers[id] = handler;
      return () => {
        delete context.handlers[id];
      };
    }
  }, [context, id, handler]);
}
